import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import DashNavbar from "./DashNavbar";
import "../css/dash.css";
import Sidebar from "./Sidebar";
import Swal from "sweetalert2";

const formatDate = (dateString) => {
  if (!dateString) return "Invalid Date";
  try {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  } catch (error) {
    return "Invalid Date";
  }
};

const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

const postingTypeConfig = {
  Job_Opening: {
    title: "Job Openings",
    icon: "fa-solid fa-briefcase",
    color: "#0d6efd",
    description: "Find your next career move with our latest job openings.",
  },
  Consultancy_Opportunity: {
    title: "Consultancy Opportunities",
    icon: "fa-solid fa-user-tie",
    color: "#198754",
    description: "Explore expert roles and consultancy projects.",
  },
  Business_Opportunity: {
    title: "Business Opportunities",
    icon: "fa-solid fa-lightbulb",
    color: "#ffc107",
    description: "Discover new business ventures and partnership opportunities.",
  },
};

const getNormalizedCategory = (job) => {
  return job?.opening_post_as;
};

export default function OpportunityListPage({ opportunityType }) {
  const [allJobPostings, setAllJobPostings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [quizStatuses, setQuizStatuses] = useState({});
  const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null);
  const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
  const [profileStatusError, setProfileStatusError] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const id = localStorage.getItem("user_id");
  const email = localStorage.getItem("email");

  const currentConfig = postingTypeConfig[opportunityType];

  const formatLocation = (locationData) => {
    if (!locationData) return "N/A";
    try {
      if (typeof locationData === "string" && locationData.startsWith("[") && locationData.endsWith("]")) {
        const validJsonString = locationData.replace(/'/g, '"');
        const locations = JSON.parse(validJsonString);
        return Array.isArray(locations) ? locations.join(", ") : locationData;
      }
      return Array.isArray(locationData) ? locationData.join(", ") : locationData;
    } catch (e) {
      return locationData;
    }
  };

  const openModal = (jobDetails) => {
    setSelectedJob(jobDetails);
    const modalElement = document.getElementById("staticBackdrop");
    if (modalElement && window.bootstrap) {
      const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
      modalInstance.show();
    }
  };

  // Function to check status for a specific job
  const checkJobStatus = useCallback(async (jobId) => {
    if (!id || !jobId) return null;
    
    try {
      const response = await axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${jobId}/`);
      return response.data;
    } catch (err) {
      // If job is not applied, API might return 404 or error
      console.error(`Error fetching status for job ${jobId}:`, err);
      return null;
    }
  }, [id]);

  // Fetch statuses for all filtered jobs
  const fetchAllQuizStatuses = useCallback(async (jobPostings) => {
    if (!id || !jobPostings || jobPostings.length === 0) return;
    
    setIsFetchingStatuses(true);
    const statusMap = {};
    
    try {
      // Create array of promises for all job status checks
      const statusPromises = jobPostings.map(async (job) => {
        if (!job?.job_id) return;
        
        try {
          const statusData = await checkJobStatus(job.job_id);
          
          // Handle API response format
          if (statusData?.success && Array.isArray(statusData.personality_results)) {
            const personalityResult = statusData.personality_results[0];
            if (personalityResult) {
              statusMap[job.job_id] = {
                applied: personalityResult.quiz_taken || false,
                status: personalityResult.status || 0,
                personality_result_id: personalityResult.personality_result_id
              };
            } else {
              // No results found, user hasn't applied
              statusMap[job.job_id] = {
                applied: false,
                status: 0
              };
            }
          } else {
            // No data or API returned false, user hasn't applied
            statusMap[job.job_id] = {
              applied: false,
              status: 0
            };
          }
        } catch (err) {
          // If API returns error (like 404), user hasn't applied
          statusMap[job.job_id] = {
            applied: false,
            status: 0
          };
        }
      });
      
      await Promise.all(statusPromises);
      setQuizStatuses(prev => ({ ...prev, ...statusMap }));
    } catch (err) {
      console.error("Error fetching statuses:", err);
    } finally {
      setIsFetchingStatuses(false);
    }
  }, [id, checkJobStatus]);

  useEffect(() => {
    setLoading(true);
    setError(null);

    axios
      .get(`${API_BASE_URL}/JobPostingresultByStatus/`)
      .then((response) => {
        const data = response.data;

        // Process job postings (preserving status field)
        const jobPostings = (data.jobpostings || []).map((job) => ({
          ...job,
          opening_post_as: "Job_Opening",
          // Status is already in the job object from API
        }));

        // Process business opportunities (add default status if not present)
        const businessOpportunities = (data.business_opportunities || []).map((b) => ({
          ...b,
          job_id: b.business_id,
          jobtitle: b.business_title,
          role_type: b.nature_of_business,
          job_location: b.locations_type,
          opening_post_as: "Business_Opportunity",
          status: b.status || "Active" // Default to Active if not present
        }));

        // Process technical consultants (add default status if not present)
        const technicalConsultants = (data.technical_consultants || []).map((c) => ({
          ...c,
          job_id: c.consultant_id,
          jobtitle: c.title,
          role_type: c.consultant_type,
          job_location: c.locations,
          opening_post_as: "Consultancy_Opportunity",
          status: c.status || "Active" // Default to Active if not present
        }));

        const allPostings = [...jobPostings, ...businessOpportunities, ...technicalConsultants];
        setAllJobPostings(allPostings);
        
        // Filter active jobs by opportunity type
        const activeFilteredByType = allPostings.filter(job => 
          getNormalizedCategory(job) === opportunityType && 
          job?.status === "Active"
        );
        
        // Fetch statuses for active opportunities only
        fetchAllQuizStatuses(activeFilteredByType);
      })
      .catch((err) => {
        setError(err);
        setAllJobPostings([]);
      })
      .finally(() => setLoading(false));
  }, [opportunityType, fetchAllQuizStatuses]);

  const filteredJobs = useMemo(() => {
    if (!Array.isArray(allJobPostings)) return [];
    
    const lowerCaseQuery = searchQuery.toLowerCase();
    
    return allJobPostings.filter((job) => {
      // Check if it's the correct opportunity type
      const isCorrectType = getNormalizedCategory(job) === opportunityType;
      
      // Check if job is active
      const isActive = job?.status === "Active";
      
      // Check if it matches search query
      const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
      
      return isCorrectType && isActive && matchesSearch;
    });
  }, [allJobPostings, searchQuery, opportunityType]);

  // Refresh status when search query changes
  useEffect(() => {
    if (filteredJobs.length > 0 && !loading) {
      // Only fetch statuses for newly filtered jobs that don't have status yet
      const jobsWithoutStatus = filteredJobs.filter(job => !quizStatuses[job.job_id]);
      if (jobsWithoutStatus.length > 0) {
        fetchAllQuizStatuses(jobsWithoutStatus);
      }
    }
  }, [searchQuery, filteredJobs, loading, quizStatuses, fetchAllQuizStatuses]);

  useEffect(() => {
    if (id && email) {
      setIsFetchingProfileStatus(true);
      axios
        .get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
        .then((response) => setProfileStatus(response.data))
        .catch(setProfileStatusError)
        .finally(() => setIsFetchingProfileStatus(false));
    }
  }, [id, email]);

  const handleApplyFromCard = (job) => {
    if (!job?.job_id) return;

    // Check if user has already applied
    const jobStatus = quizStatuses[job.job_id];
    if (jobStatus?.applied) {
      Swal.fire({
        icon: "info",
        title: "Already Applied",
        text: `You have already applied for this position with a score of ${jobStatus.status}%.`,
        confirmButtonText: "OK"
      });
      return;
    }

    if (!profileStatus?.Resume_Filled) {
      Swal.fire({
        icon: "warning",
        title: "Profile Incomplete",
        html: "Please complete your profile before applying.",
        confirmButtonText: "Go to My Profile",
        showCancelButton: true,
      }).then((result) => {
        if (result.isConfirmed) navigate("/jobapplication");
      });
      return;
    }

    localStorage.removeItem("job_id");
    localStorage.removeItem("business_id");
    localStorage.removeItem("consultant_id");
    localStorage.removeItem("quiz_type");
    localStorage.removeItem("selected_posting_type");

    const type = job.opening_post_as;
    localStorage.setItem("selectedJobTitle", job.jobtitle);
    localStorage.setItem("job_id", job.job_id);

    if (type === "Consultancy_Opportunity") {
      localStorage.setItem("quiz_type", "consultant");
      localStorage.setItem("selected_posting_type", "Technical_Consultant");
      localStorage.setItem("consultant_id", job.job_id);
    } else if (type === "Business_Opportunity") {
      localStorage.setItem("quiz_type", "business");
      localStorage.setItem("selected_posting_type", "Business_Opportunity");
      localStorage.setItem("business_id", job.job_id);
    } else {
      localStorage.setItem("quiz_type", "job");
      localStorage.setItem("selected_posting_type", "Job_Opening");
    }

    navigate("/personalityquizform", { state: { from: location.pathname } });
  };

  const getButtonContent = (job) => {
    const jobStatus = quizStatuses[job.job_id];
    
    if (isFetchingStatuses || isFetchingProfileStatus) {
      return <span className="spinner-border spinner-border-sm" />;
    }
    
    if (jobStatus?.applied) {
      return (
        <>
          <span className="ms-1">Applied</span>
        </>
      );
    }
    
    return "Apply";
  };

  const renderJobCard = (job) => {
    if (!job?.job_id) return null;
    const jobStatus = quizStatuses[job.job_id];
    const hasApplied = jobStatus?.applied || false;
    const statusPercentage = jobStatus?.status || 0;
    
    return (
      <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
        <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
          <div className="card-body d-flex flex-column flex-grow-1">
            <div>
              <div className="d-flex justify-content-between align-items-start mb-2">
                <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
                  {job.jobtitle || "No Title"}
                </h5>
                <span className={`badge ${job.status === "Active" ? "bg-success" : "bg-secondary"}`}>
                  {job.status || "Inactive"}
                </span>
              </div>
              <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
              {hasApplied && (
                <div className="mb-2">
                  <div className="d-flex align-items-center">
                    <div className="progress flex-grow-1" style={{ height: "6px" }}>
                      <div 
                        className="progress-bar bg-success" 
                        role="progressbar" 
                        style={{ width: `${statusPercentage}%` }}
                        aria-valuenow={statusPercentage} 
                        aria-valuemin="0" 
                        aria-valuemax="100"
                      />
                    </div>
                    <small className="text-success ms-2 fw-bold">{statusPercentage}%</small>
                  </div>
                  <small className="text-muted">Quiz Score</small>
                </div>
              )}
            </div>
            <div className="mt-auto">
              <div className="mb-3">
                {opportunityType !== "Business_Opportunity" && (
                  <p className="card-text small d-flex align-items-center">
                    <i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" />
                    <span>{job.experience || "N/A"}</span>
                  </p>
                )}
                <p className="card-text small d-flex align-items-center">
                  <i className="fa-solid fa-clock fa-fw me-2 text-secondary" />
                  <span>{job.role_type || "N/A"}</span>
                </p>
                <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}>
                  <i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" />
                  <span>{formatLocation(job.job_location)}</span>
                </p>
              </div>
            </div>
          </div>
          <div className="card-footer bg-white border-top-0 pb-3">
            <div className="d-flex justify-content-between">
              <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>
                Details
              </button>
              <button
                type="button"
                className={`btn w-100 ms-1 ${hasApplied ? "btn-secondary" : "btn-success"}`}
                onClick={() => handleApplyFromCard(job)}
                disabled={isFetchingStatuses || isFetchingProfileStatus}
              >
                {getButtonContent(job)}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <DashNavbar />
      <div className="wrapper">
        <Sidebar />
        <div className="main">
          {loading && (
            <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
              <div className="spinner-border text-primary"></div>
            </div>
          )}
          {!loading && error && (
            <div className="alert alert-danger m-4" role="alert">
              Error loading opportunities: {error.message}
            </div>
          )}
          {!loading && !error && (
            <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
              <div className="text-center mb-5">
                <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
                <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
                <p className="lead text-muted">{currentConfig.description}</p>
              </div>
              <div className="row justify-content-center mb-5">
                <div className="col-lg-6 col-md-8">
                  <input
                    type="search"
                    className="form-control form-control-lg"
                    placeholder={`Search ${currentConfig.title}...`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>
              <section className="mb-5">
                {filteredJobs.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fas fa-search fa-3x text-muted mb-3"></i>
                    <h4>No Active Opportunities Found</h4>
                    <p className="text-muted">
                      {searchQuery 
                        ? `No active "${currentConfig.title}" match your search criteria.`
                        : `No active "${currentConfig.title}" are currently available.`}
                    </p>
                  </div>
                ) : (
                  <div className="row">
                    {filteredJobs.map((job) => renderJobCard(job))}
                  </div>
                )}
              </section>
            </div>
          )}
        </div>
      </div>

      <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
        <div className="modal-dialog modal-dialog-scrollable modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
              <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
            </div>
            <div className="modal-body" style={{ textAlign: "justify" }}>
              {selectedJob ? (
                <>
                  <div className="mb-3">
                    <span className={`badge ${selectedJob.status === "Active" ? "bg-success" : "bg-secondary"}`}>
                      {selectedJob.status || "Inactive"}
                    </span>
                  </div>
                  {opportunityType === "Business_Opportunity" ? (
                    <>
                      <p>
                        <strong>Business ID – </strong> ({selectedJob.business_id || "N/A"})
                      </p>
                      <p>
                        <strong>Business Title – </strong> {selectedJob.business_title || "N/A"}
                      </p>
                      <p>
                        <strong>Nature of Business – </strong> {selectedJob.nature_of_business || "N/A"}
                      </p>
                      <p>
                        <strong>Locations- </strong> {formatLocation(selectedJob.locations_type)}
                      </p>
                      <p>
                        <strong>Type – </strong> Business Opportunity
                      </p>
                    </>
                  ) : opportunityType === "Consultancy_Opportunity" ? (
                    <>
                      <p>
                        <strong>ID – </strong> ({selectedJob.consultant_id || "N/A"})
                      </p>
                      <p>
                        <strong>Title – </strong> {selectedJob.title || "N/A"}
                      </p>
                      <p>
                        <strong>Vacancies- </strong> {selectedJob.vacancies || "N/A"}
                      </p>
                      <p>
                        <strong>Experience – </strong> {selectedJob.experience || "N/A"} years
                      </p>
                      <p>
                        <strong>Locations – </strong> {formatLocation(selectedJob.locations)}
                      </p>
                      <p>
                        <strong>Type – </strong> {selectedJob.consultant_type || "N/A"}
                      </p>
                    </>
                  ) : (
                    <>
                      <p>
                        <strong>Job ID – </strong> ({selectedJob.job_id || "N/A"})
                      </p>
                      <p>
                        <strong>Job Title – </strong> {selectedJob.jobtitle || "N/A"}
                      </p>
                      <p>
                        <strong>Vacancies- </strong> {selectedJob.vacancies || "N/A"}
                      </p>
                      <p>
                        <strong>Experience – </strong> {selectedJob.experience || "N/A"} years
                      </p>
                      <p>
                        <strong>Locations – </strong> {formatLocation(selectedJob.job_location)}
                      </p>
                      <p>
                        <strong>Type – </strong> {selectedJob.role_type || "N/A"}
                      </p>
                    </>
                  )}
                  <hr />
                  <h6>Description:</h6>
                  <div
                    style={{ wordWrap: "break-word" }}
                    dangerouslySetInnerHTML={{ __html: selectedJob.description || "No description provided." }}
                  />
                </>
              ) : (
                <p>Loading...</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";
 
// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };
 
// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";
 
// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };
 
// const getNormalizedCategory = (job) => {
//     return job?.opening_post_as;
// };
 
// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);
 
//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");
 
//   const currentConfig = postingTypeConfig[opportunityType];
 
//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };
 
//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };
 
//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);
 
//   useEffect(() => {
//     setLoading(true);
//     setError(null);
 
//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;
 
//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening'
//         }));
 
//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           opening_post_as: 'Business_Opportunity'
//         }));
 
//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id,
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             opening_post_as: 'Consultancy_Opportunity'
//         }));
 
//         const allPostings = [...jobPostings, ...businessOpportunities, ...technicalConsultants];
//         setAllJobPostings(allPostings);
//         fetchQuizStatuses(allPostings);
//       })
//       .catch(err => { setError(err); setAllJobPostings([]); })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);
 
//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     }
//   }, [id, email]);
 
//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) return [];
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);
 
//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) return;
   
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => { if (result.isConfirmed) navigate("/jobapplication"); });
//       return;
//     }
 
//     // 1. Clear previous storage strictly
//     localStorage.removeItem("job_id");
//     localStorage.removeItem("business_id");
//     localStorage.removeItem("consultant_id");
//     localStorage.removeItem("quiz_type");
//     localStorage.removeItem("selected_posting_type");
 
//     const type = job.opening_post_as;
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
   
//     // As per your successful Business Opportunity screenshot:
//     // The payload needs 'job_id' (generic key) and 'quiz_type'
//     localStorage.setItem("job_id", job.job_id);
 
//     if (type === 'Consultancy_Opportunity') {
//         localStorage.setItem("quiz_type", "consultant");
//         localStorage.setItem("selected_posting_type", "Technical_Consultant"); // For fetching questions
//         localStorage.setItem("consultant_id", job.job_id);
//     }
//     else if (type === 'Business_Opportunity') {
//         localStorage.setItem("quiz_type", "business");
//         localStorage.setItem("selected_posting_type", "Business_Opportunity");
//         localStorage.setItem("business_id", job.job_id);
//     }
//     else {
//         localStorage.setItem("quiz_type", "job");
//         localStorage.setItem("selected_posting_type", "Job_Opening");
//     }
 
//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };
 
//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;
//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 {opportunityType !== 'Business_Opportunity' && (
//                   <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 )}
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button
//                 type="button"
//                 className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`}
//                 onClick={() => !hasTakenQuiz && handleApplyFromCard(job)}
//                 disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}
//               >
//                 {isFetchingStatuses || isFetchingProfileStatus ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };
 
//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary"></div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>
//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                 </div>
//               </div>
//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? filteredJobs.map(job => renderJobCard(job)) : <p className="text-center text-muted">No results found.</p>}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>
 
//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body" style={{ textAlign: 'justify' }}>
//               {selectedJob ? (
//                 <>
//                   {opportunityType === "Business_Opportunity" ? (
//                     <>
//                       <p><strong>Business ID – </strong> ({selectedJob.business_id || 'N/A'})</p>
//                       <p><strong>Business Title – </strong> {selectedJob.business_title || 'N/A'}</p>
//                       <p><strong>Nature of Business – </strong> {selectedJob.nature_of_business || 'N/A'}</p>
//                       <p><strong>Locations- </strong> {formatLocation(selectedJob.locations_type)}</p>
//                       <p><strong>Type – </strong> Business Opportunity</p>
//                     </>
//                   ) : opportunityType === "Consultancy_Opportunity" ? (
//                     <>
//                       <p><strong>ID – </strong> ({selectedJob.consultant_id || 'N/A'})</p>
//                       <p><strong>Title – </strong> {selectedJob.title || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.locations)}</p>
//                       <p><strong>Type – </strong> {selectedJob.consultant_type || 'N/A'}</p>
//                     </>
//                   ) : (
//                     <>
//                       <p><strong>Job ID – </strong> ({selectedJob.job_id || 'N/A'})</p>
//                       <p><strong>Job Title – </strong> {selectedJob.jobtitle || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.job_location)}</p>
//                       <p><strong>Type – </strong> {selectedJob.role_type || 'N/A'}</p>
//                     </>
//                   )}
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }


// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//   return job?.opening_post_as;
// };

// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({ ids: new Set(), titles: new Set() });
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return "N/A";
//     try {
//       if (typeof locationData === "string" && locationData.startsWith("[") && locationData.endsWith("]")) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(", ") : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(", ") : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async () => {
//     if (!email) return;
//     setIsFetchingStatuses(true);
//     try {
//       const response = await axios.get(`${API_BASE_URL}/applicants_alt/`);
//       const currentUserData = response.data.find(
//         (app) => app.email?.toLowerCase() === email.toLowerCase()
//       );

//       if (currentUserData && currentUserData.action && currentUserData.action.personality_results) {
//         const appliedIds = new Set();
//         const appliedTitles = new Set();

//         currentUserData.action.personality_results.forEach((res) => {
//           if (res.quiz_taken === true) {
//             // Track by Type + Object ID
//             if (res.opening_post && res.object_id) {
//               appliedIds.add(`${res.opening_post}_${res.object_id}`);
//             }
//             // Track by Title (fallback for safety)
//             if (res.job_title) {
//               appliedTitles.add(res.job_title.trim().toLowerCase());
//             }
//           }
//         });
//         setQuizStatuses({ ids: appliedIds, titles: appliedTitles });
//       }
//     } catch (err) {
//       console.error("Error fetching quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [email]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios
//       .get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then((response) => {
//         const data = response.data;

//         const jobPostings = (data.jobpostings || []).map((job) => ({
//           ...job,
//           opening_post_as: "Job_Opening",
//         }));

//         const businessOpportunities = (data.business_opportunities || []).map((b) => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           opening_post_as: "Business_Opportunity",
//         }));

//         const technicalConsultants = (data.technical_consultants || []).map((c) => ({
//           ...c,
//           job_id: c.consultant_id,
//           jobtitle: c.title,
//           role_type: c.consultant_type,
//           job_location: c.locations,
//           opening_post_as: "Consultancy_Opportunity",
//         }));

//         const allPostings = [...jobPostings, ...businessOpportunities, ...technicalConsultants];
//         setAllJobPostings(allPostings);
//         fetchQuizStatuses();
//       })
//       .catch((err) => {
//         setError(err);
//         setAllJobPostings([]);
//       })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios
//         .get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then((response) => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) return [];
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter((job) => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) return;
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => {
//         if (result.isConfirmed) navigate("/jobapplication");
//       });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     if (job.opening_post_as) localStorage.setItem("selected_posting_type", job.opening_post_as);
//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;

//     // Check by Type + ID key
//     const statusKey = `${job.opening_post_as}_${job.job_id}`;
//     // Check by Title fallback
//     const titleKey = job.jobtitle?.trim().toLowerCase();

//     const hasTakenQuiz = 
//         quizStatuses.ids.has(statusKey) || 
//         quizStatuses.titles.has(titleKey);

//     return (
//       <div key={statusKey} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 {opportunityType !== "Business_Opportunity" && (
//                   <p className="card-text small d-flex align-items-center">
//                     <i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" />
//                     <span>{job.experience || "N/A"}</span>
//                   </p>
//                 )}
//                 <p className="card-text small d-flex align-items-center">
//                   <i className="fa-solid fa-clock fa-fw me-2 text-secondary" />
//                   <span>{job.role_type || "N/A"}</span>
//                 </p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}>
//                   <i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" />
//                   <span>{formatLocation(job.job_location)}</span>
//                 </p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>
//                 Details
//               </button>
//               <button
//                 type="button"
//                 className={`btn w-100 ms-1 ${hasTakenQuiz ? "btn-secondary" : "btn-success"}`}
//                 onClick={() => !hasTakenQuiz && handleApplyFromCard(job)}
//                 disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}
//               >
//                 {isFetchingStatuses || isFetchingProfileStatus ? (
//                   <span className="spinner-border spinner-border-sm" />
//                 ) : hasTakenQuiz ? (
//                   "Applied"
//                 ) : (
//                   "Apply"
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && (
//             <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
//               <div className="spinner-border text-primary"></div>
//             </div>
//           )}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>
//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <input
//                     type="search"
//                     className="form-control form-control-lg"
//                     placeholder={`Search ${currentConfig.title}...`}
//                     value={searchQuery}
//                     onChange={(e) => setSearchQuery(e.target.value)}
//                   />
//                 </div>
//               </div>
//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? (
//                     filteredJobs.map((job) => renderJobCard(job))
//                   ) : (
//                     <p className="text-center text-muted">No results found.</p>
//                   )}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body" style={{ textAlign: "justify" }}>
//               {selectedJob ? (
//                 <>
//                   {opportunityType === "Business_Opportunity" ? (
//                     <>
//                       <p>
//                         <strong>Business ID – </strong> ({selectedJob.business_id || "N/A"})
//                       </p>
//                       <p>
//                         <strong>Business Title – </strong> {selectedJob.business_title || "N/A"}
//                       </p>
//                       <p>
//                         <strong>Nature of Business – </strong> {selectedJob.nature_of_business || "N/A"}
//                       </p>
//                       <p>
//                         <strong>Locations- </strong> {formatLocation(selectedJob.locations_type)}
//                       </p>
//                       <p>
//                         <strong>Type – </strong> Business Opportunity
//                       </p>
//                     </>
//                   ) : opportunityType === "Consultancy_Opportunity" ? (
//                     <>
//                       <p>
//                         <strong>ID – </strong> ({selectedJob.consultant_id || "N/A"})
//                       </p>
//                       <p>
//                         <strong>Title – </strong> {selectedJob.title || "N/A"}
//                       </p>
//                       <p>
//                         <strong>Vacancies- </strong> {selectedJob.vacancies || "N/A"}
//                       </p>
//                       <p>
//                         <strong>Experience – </strong> {selectedJob.experience || "N/A"} years
//                       </p>
//                       <p>
//                         <strong>Locations – </strong> {formatLocation(selectedJob.locations)}
//                       </p>
//                       <p>
//                         <strong>Type – </strong> {selectedJob.consultant_type || "N/A"}
//                       </p>
//                     </>
//                   ) : (
//                     <>
//                       <p>
//                         <strong>Job ID – </strong> ({selectedJob.job_id || "N/A"})
//                       </p>
//                       <p>
//                         <strong>Job Title – </strong> {selectedJob.jobtitle || "N/A"}
//                       </p>
//                       <p>
//                         <strong>Vacancies- </strong> {selectedJob.vacancies || "N/A"}
//                       </p>
//                       <p>
//                         <strong>Experience – </strong> {selectedJob.experience || "N/A"} years
//                       </p>
//                       <p>
//                         <strong>Locations – </strong> {formatLocation(selectedJob.job_location)}
//                       </p>
//                       <p>
//                         <strong>Type – </strong> {selectedJob.role_type || "N/A"}
//                       </p>
//                     </>
//                   )}
//                   <hr />
//                   <h6>Description:</h6>
//                   <div
//                     style={{ wordWrap: "break-word" }}
//                     dangerouslySetInnerHTML={{ __html: selectedJob.description || "No description provided." }}
//                   />
//                 </>
//               ) : (
//                 <p>Loading...</p>
//               )}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }

// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//     return job?.opening_post_as;
// };

// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;

//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening'
//         }));

//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           opening_post_as: 'Business_Opportunity'
//         }));

//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id,
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             opening_post_as: 'Consultancy_Opportunity'
//         }));

//         const allPostings = [...jobPostings, ...businessOpportunities, ...technicalConsultants];
//         setAllJobPostings(allPostings);
//         fetchQuizStatuses(allPostings);
//       })
//       .catch(err => { setError(err); setAllJobPostings([]); })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) return [];
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) return;
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => { if (result.isConfirmed) navigate("/jobapplication"); });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     if (job.opening_post_as) localStorage.setItem("selected_posting_type", job.opening_post_as);
//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;
//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 {opportunityType !== 'Business_Opportunity' && (
//                   <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 )}
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button 
//                 type="button" 
//                 className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} 
//                 onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} 
//                 disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}
//               >
//                 {isFetchingStatuses || isFetchingProfileStatus ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary"></div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>
//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                 </div>
//               </div>
//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? filteredJobs.map(job => renderJobCard(job)) : <p className="text-center text-muted">No results found.</p>}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body" style={{ textAlign: 'justify' }}>
//               {selectedJob ? (
//                 <>
//                   {opportunityType === "Business_Opportunity" ? (
//                     <>
//                       <p><strong>Business ID – </strong> ({selectedJob.business_id || 'N/A'})</p>
//                       <p><strong>Business Title – </strong> {selectedJob.business_title || 'N/A'}</p>
//                       <p><strong>Nature of Business – </strong> {selectedJob.nature_of_business || 'N/A'}</p>
//                       <p><strong>Locations- </strong> {formatLocation(selectedJob.locations_type)}</p>
//                       <p><strong>Type – </strong> Business Opportunity</p>
//                     </>
//                   ) : opportunityType === "Consultancy_Opportunity" ? (
//                     <>
//                       <p><strong>ID – </strong> ({selectedJob.consultant_id || 'N/A'})</p>
//                       <p><strong>Title – </strong> {selectedJob.title || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.locations)}</p>
//                       <p><strong>Type – </strong> {selectedJob.consultant_type || 'N/A'}</p>
//                     </>
//                   ) : (
//                     <>
//                       <p><strong>Job ID – </strong> ({selectedJob.job_id || 'N/A'})</p>
//                       <p><strong>Job Title – </strong> {selectedJob.jobtitle || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.job_location)}</p>
//                       <p><strong>Type – </strong> {selectedJob.role_type || 'N/A'}</p>
//                     </>
//                   )}
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }


// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//     return job?.opening_post_as;
// };

// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;

//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening'
//         }));

//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id, // Map unique ID to job_id for quiz check
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           opening_post_as: 'Business_Opportunity'
//         }));

//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id, // Map unique ID to job_id for quiz check
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             opening_post_as: 'Consultancy_Opportunity'
//         }));

//         const allPostings = [...jobPostings, ...businessOpportunities, ...technicalConsultants];
//         setAllJobPostings(allPostings);
//         fetchQuizStatuses(allPostings);
//       })
//       .catch(err => { setError(err); setAllJobPostings([]); })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) return [];
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) return;
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => { if (result.isConfirmed) navigate("/jobapplication"); });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     if (job.opening_post_as) localStorage.setItem("selected_posting_type", job.opening_post_as);
//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;
//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 {opportunityType !== 'Business_Opportunity' && (
//                   <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 )}
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button 
//                 type="button" 
//                 className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} 
//                 onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} 
//                 disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}
//               >
//                 {isFetchingStatuses || isFetchingProfileStatus ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary"></div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>
//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                 </div>
//               </div>
//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? filteredJobs.map(job => renderJobCard(job)) : <p className="text-center text-muted">No results found.</p>}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body" style={{ textAlign: 'justify' }}>
//               {selectedJob ? (
//                 <>
//                   {opportunityType === "Business_Opportunity" ? (
//                     <>
//                       <p><strong>Business ID – </strong> ({selectedJob.business_id || 'N/A'})</p>
//                       <p><strong>Business Title – </strong> {selectedJob.business_title || 'N/A'}</p>
//                       <p><strong>Nature of Business – </strong> {selectedJob.nature_of_business || 'N/A'}</p>
//                       <p><strong>Locations- </strong> {formatLocation(selectedJob.locations_type)}</p>
//                       <p><strong>Type – </strong> Business Opportunity</p>
//                     </>
//                   ) : opportunityType === "Consultancy_Opportunity" ? (
//                     <>
//                       <p><strong>ID – </strong> ({selectedJob.consultant_id || 'N/A'})</p>
//                       <p><strong>Title – </strong> {selectedJob.title || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.locations)}</p>
//                       <p><strong>Type – </strong> {selectedJob.consultant_type || 'N/A'}</p>
//                     </>
//                   ) : (
//                     <>
//                       <p><strong>Job ID – </strong> ({selectedJob.job_id || 'N/A'})</p>
//                       <p><strong>Job Title – </strong> {selectedJob.jobtitle || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.job_location)}</p>
//                       <p><strong>Type – </strong> {selectedJob.role_type || 'N/A'}</p>
//                     </>
//                   )}
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }







// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//     return job?.opening_post_as;
// };

// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;

//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening'
//         }));

//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           opening_post_as: 'Business_Opportunity'
//         }));

//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id,
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             opening_post_as: 'Consultancy_Opportunity'
//         }));

//         const allPostings = [...jobPostings, ...businessOpportunities, ...technicalConsultants];
//         setAllJobPostings(allPostings);
//         fetchQuizStatuses(allPostings);
//       })
//       .catch(err => { setError(err); setAllJobPostings([]); })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) return [];
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) return;
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => { if (result.isConfirmed) navigate("/jobapplication"); });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     if (job.opening_post_as) localStorage.setItem("selected_posting_type", job.opening_post_as);
//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;
//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 {opportunityType !== 'Business_Opportunity' && (
//                   <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 )}
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz}>
//                 {hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary"></div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>
//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                 </div>
//               </div>
//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? filteredJobs.map(job => renderJobCard(job)) : <p className="text-center text-muted">No results found.</p>}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body" style={{ textAlign: 'justify' }}>
//               {selectedJob ? (
//                 <>
//                   {opportunityType === "Business_Opportunity" ? (
//                     <>
//                       <p><strong>Business ID – </strong> ({selectedJob.business_id || 'N/A'})</p>
//                       <p><strong>Business Title – </strong> {selectedJob.business_title || 'N/A'}</p>
//                       <p><strong>Nature of Business – </strong> {selectedJob.nature_of_business || 'N/A'}</p>
//                       <p><strong>Locations- </strong> {formatLocation(selectedJob.locations_type)}</p>
//                       <p><strong>Type – </strong> Business Opportunity</p>
//                     </>
//                   ) : opportunityType === "Consultancy_Opportunity" ? (
//                     <>
//                       <p><strong>ID – </strong> ({selectedJob.consultant_id || 'N/A'})</p>
//                       <p><strong>Title – </strong> {selectedJob.title || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.locations)}</p>
//                       <p><strong>Type – </strong> {selectedJob.consultant_type || 'N/A'}</p>
//                     </>
//                   ) : (
//                     <>
//                       <p><strong>Job ID – </strong> ({selectedJob.job_id || 'N/A'})</p>
//                       <p><strong>Job Title – </strong> {selectedJob.jobtitle || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.job_location)}</p>
//                       <p><strong>Type – </strong> {selectedJob.role_type || 'N/A'}</p>
//                     </>
//                   )}
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }





// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//     return job?.opening_post_as;
// };

// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;

//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening'
//         }));

//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           opening_post_as: 'Business_Opportunity'
//         }));

//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id,
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             opening_post_as: 'Consultancy_Opportunity'
//         }));

//         const allPostings = [...jobPostings, ...businessOpportunities, ...technicalConsultants];
//         setAllJobPostings(allPostings);
//         fetchQuizStatuses(allPostings);
//       })
//       .catch(err => { setError(err); setAllJobPostings([]); })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) return [];
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) return;
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => { if (result.isConfirmed) navigate("/jobapplication"); });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     if (job.opening_post_as) localStorage.setItem("selected_posting_type", job.opening_post_as);
//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;
//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 {opportunityType !== 'Business_Opportunity' && (
//                   <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 )}
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz}>
//                 {hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary"></div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>
//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                 </div>
//               </div>
//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? filteredJobs.map(job => renderJobCard(job)) : <p className="text-center text-muted">No results found.</p>}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body">
//               {selectedJob ? (
//                 <>
//                   {opportunityType === "Business_Opportunity" ? (
//                     <>
//                       <p><strong>Business ID – </strong> ({selectedJob.business_id || 'N/A'})</p>
//                       <p><strong>Business Title – </strong> {selectedJob.business_title || 'N/A'}</p>
//                       <p><strong>Nature of Business – </strong> {selectedJob.nature_of_business || 'N/A'}</p>
//                       <p><strong>Locations- </strong> {formatLocation(selectedJob.locations_type)}</p>
//                       <p><strong>Type – </strong> Business Opportunity</p>
//                     </>
//                   ) : opportunityType === "Consultancy_Opportunity" ? (
//                     <>
//                       <p><strong>ID – </strong> ({selectedJob.consultant_id || 'N/A'})</p>
//                       <p><strong>Title – </strong> {selectedJob.title || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.locations)}</p>
//                       <p><strong>Type – </strong> {selectedJob.consultant_type || 'N/A'}</p>
//                     </>
//                   ) : (
//                     <>
//                       <p><strong>Job ID – </strong> ({selectedJob.job_id || 'N/A'})</p>
//                       <p><strong>Job Title – </strong> {selectedJob.jobtitle || 'N/A'}</p>
//                       <p><strong>Vacancies- </strong> {selectedJob.vacancies || 'N/A'}</p>
//                       <p><strong>Experience – </strong> {selectedJob.experience || 'N/A'} years</p>
//                       <p><strong>Locations – </strong> {formatLocation(selectedJob.job_location)}</p>
//                       <p><strong>Type – </strong> {selectedJob.role_type || 'N/A'}</p>
//                     </>
//                   )}
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word', textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//     </>
//   );
// }




// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//     // This function now primarily relies on the `opening_post_as` field
//     // which we normalize during the data fetching process.
//     return job?.opening_post_as;
// };


// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;

//         // Normalize job postings
//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening' // Ensure correct category
//         }));

//         // Normalize business opportunities
//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           experience: b.experience || 'N/A',
//           vacancies: b.vacancies || 'N/A',
//           opening_post_as: 'Business_Opportunity'
//         }));

//         // Normalize technical consultants
//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id,
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             experience: c.experience || 'N/A',
//             vacancies: c.vacancies || 'N/A',
//             opening_post_as: 'Consultancy_Opportunity'
//         }));

//         const allPostings = [
//           ...jobPostings,
//           ...businessOpportunities,
//           ...technicalConsultants
//         ];

//         setAllJobPostings(allPostings);

//         if (allPostings.length > 0) {
//           fetchQuizStatuses(allPostings);
//         }
//       })
//       .catch(err => {
//         console.error("Failed to fetch postings:", err);
//         setError(err);
//         setAllJobPostings([]);
//       })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     } else if (id && !email) {
//       setProfileStatusError({ message: "User email not found." });
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) {
//       return [];
//     }
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) {
//       Swal.fire("Error", "Cannot proceed without valid job data.", "error");
//       return;
//     }
//     if (isFetchingProfileStatus) {
//       Swal.fire({ title: "Checking Profile...", icon: "info", didOpen: () => Swal.showLoading() });
//       return;
//     }
//     if (profileStatusError) {
//       Swal.fire("Profile Check Failed", profileStatusError.message, "error");
//       return;
//     }
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile (e.g., resume missing) before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => {
//         if (result.isConfirmed) navigate("/jobapplication");
//       });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     // Persist the posting type so the quiz form can fetch the correct questions set
//     if (job.opening_post_as) {
//       localStorage.setItem("selected_posting_type", job.opening_post_as);
//     }

//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;

//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}>
//                 {isFetchingProfileStatus || isFetchingStatuses ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div></div>}
//           {error && <div className="container mt-4"><div className="alert alert-danger">Error loading data: {error.message}</div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>

//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <div className="input-group shadow-sm">
//                     <span className="input-group-text"><i className="fas fa-search"></i></span>
//                     <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                   </div>
//                 </div>
//               </div>

//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? (
//                     filteredJobs.map(job => renderJobCard(job))
//                   ) : (
//                     <div className="col-12 text-center text-muted mt-4">
//                         <i className="fas fa-inbox fa-3x mb-3"></i>
//                         <h4>No Results Found</h4>
//                         <p className="fst-italic">
//                             {searchQuery ? `No ${currentConfig.title.toLowerCase()} match your search.` : `No ${currentConfig.title.toLowerCase()} are available at this time.`}
//                         </p>
//                     </div>
//                   )}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body">
//               {selectedJob ? (
//                 <>
//                   <p>
//                     <strong>Type:</strong>{" "}
//                     <span className="badge fs-6" style={{ backgroundColor: currentConfig.color }}>
//                       {currentConfig.title}
//                     </span>
//                   </p>
//                   <p><strong>Role Type:</strong> {selectedJob.role_type || 'N/A'}</p>
//                   <p><strong>Experience:</strong> {selectedJob.experience || 'N/A'}</p>
//                   <p><strong>Location(s):</strong> {formatLocation(selectedJob.job_location)}</p>
//                   <p><strong>Vacancies:</strong> {selectedJob.vacancies || 'N/A'}</p>
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word', textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .job-card-hover {
//             transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
//             border: 1px solid #dee2e6;
//         }
//         .job-card-hover:hover {
//             transform: translateY(-5px);
//             box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15) !important;
//             border-color: ${currentConfig.color};
//         }
//         .card-title-clamp {
//             display: -webkit-box;
//             -webkit-line-clamp: 2;
//             -webkit-box-orient: vertical;
//             overflow: hidden;
//             text-overflow: ellipsis;
//             min-height: 3rem; 
//         }
//       `}</style>
//     </>
//   );
// }






// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// // MODIFIED: Changed 'Consultancy_Opportunity' to 'Technical_Consultant' to align with the applicant page
// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Technical_Consultant: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//     return job?.opening_post_as;
// };


// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;

//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening'
//         }));

//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           experience: b.experience || 'N/A',
//           vacancies: b.vacancies || 'N/A',
//           opening_post_as: 'Business_Opportunity'
//         }));

//         // MODIFIED: Set 'opening_post_as' to 'Technical_Consultant'
//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id,
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             experience: c.experience || 'N/A',
//             vacancies: c.vacancies || 'N/A',
//             opening_post_as: 'Technical_Consultant' // This now matches the applicant page's filter
//         }));

//         const allPostings = [
//           ...jobPostings,
//           ...businessOpportunities,
//           ...technicalConsultants
//         ];

//         setAllJobPostings(allPostings);

//         if (allPostings.length > 0) {
//           fetchQuizStatuses(allPostings);
//         }
//       })
//       .catch(err => {
//         console.error("Failed to fetch postings:", err);
//         setError(err);
//         setAllJobPostings([]);
//       })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     } else if (id && !email) {
//       setProfileStatusError({ message: "User email not found." });
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) {
//       return [];
//     }
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) {
//       Swal.fire("Error", "Cannot proceed without valid job data.", "error");
//       return;
//     }
//     if (isFetchingProfileStatus) {
//       Swal.fire({ title: "Checking Profile...", icon: "info", didOpen: () => Swal.showLoading() });
//       return;
//     }
//     if (profileStatusError) {
//       Swal.fire("Profile Check Failed", profileStatusError.message, "error");
//       return;
//     }
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile (e.g., resume missing) before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => {
//         if (result.isConfirmed) navigate("/jobapplication");
//       });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     // This now correctly saves 'Technical_Consultant' when applying for a consultancy role
//     if (job.opening_post_as) {
//       localStorage.setItem("selected_posting_type", job.opening_post_as);
//     }

//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;

//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}>
//                 {isFetchingProfileStatus || isFetchingStatuses ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div></div>}
//           {error && <div className="container mt-4"><div className="alert alert-danger">Error loading data: {error.message}</div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>

//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <div className="input-group shadow-sm">
//                     <span className="input-group-text"><i className="fas fa-search"></i></span>
//                     <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                   </div>
//                 </div>
//               </div>

//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? (
//                     filteredJobs.map(job => renderJobCard(job))
//                   ) : (
//                     <div className="col-12 text-center text-muted mt-4">
//                         <i className="fas fa-inbox fa-3x mb-3"></i>
//                         <h4>No Results Found</h4>
//                         <p className="fst-italic">
//                             {searchQuery ? `No ${currentConfig.title.toLowerCase()} match your search.` : `No ${currentConfig.title.toLowerCase()} are available at this time.`}
//                         </p>
//                     </div>
//                   )}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body">
//               {selectedJob ? (
//                 <>
//                   <p>
//                     <strong>Type:</strong>{" "}
//                     <span className="badge fs-6" style={{ backgroundColor: currentConfig.color }}>
//                       {currentConfig.title}
//                     </span>
//                   </p>
//                   <p><strong>Role Type:</strong> {selectedJob.role_type || 'N/A'}</p>
//                   <p><strong>Experience:</strong> {selectedJob.experience || 'N/A'}</p>
//                   <p><strong>Location(s):</strong> {formatLocation(selectedJob.job_location)}</p>
//                   <p><strong>Vacancies:</strong> {selectedJob.vacancies || 'N/A'}</p>
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word', textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .job-card-hover {
//             transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
//             border: 1px solid #dee2e6;
//         }
//         .job-card-hover:hover {
//             transform: translateY(-5px);
//             box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15) !important;
//             border-color: ${currentConfig.color};
//         }
//         .card-title-clamp {
//             display: -webkit-box;
//             -webkit-line-clamp: 2;
//             -webkit-box-orient: vertical;
//             overflow: hidden;
//             text-overflow: ellipsis;
//             min-height: 3rem; 
//         }
//       `}</style>
//     </>
//   );
// }



//   import React, { useState, useEffect, useCallback, useMemo } from "react";
//   import axios from "axios";
//   // --- MODIFIED: Import useLocation ---
//   import { useNavigate, useLocation } from "react-router-dom";
//   import DashNavbar from "./DashNavbar";
//   import "../css/dash.css";
//   import Sidebar from "./Sidebar";
//   import Swal from "sweetalert2";

//   // --- Helper Functions & Constants ---
//   const formatDate = (dateString) => {
//     if (!dateString) return "Invalid Date";
//     try {
//       const options = { year: "numeric", month: "long", day: "numeric" };
//       return new Date(dateString).toLocaleDateString(undefined, options);
//     } catch (error) {
//       return "Invalid Date";
//     }
//   };

//   const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/";

//   const postingTypeConfig = {
//     Job_Opening: {
//       title: "Job Openings",
//       icon: "fa-solid fa-briefcase",
//       color: "#0d6efd",
//       description: "Find your next career move with our latest job openings.",
//     },
//     Consultancy_Opportunity: {
//       title: "Consultancy Opportunities",
//       icon: "fa-solid fa-user-tie",
//       color: "#198754",
//       description: "Explore expert roles and consultancy projects.",
//     },
//     Business_Opportunity: {
//       title: "Business Opportunities",
//       icon: "fa-solid fa-lightbulb",
//       color: "#ffc107",
//       description: "Discover new business ventures and partnership opportunities.",
//     },
//   };

//   const getNormalizedCategory = (job) => {
//     const category = job?.opening_post_as;
//     if (!category || category === "Job Opening" || category === "Job_Opening") {
//       return "Job_Opening";
//     }
//     return category;
//   };

//   export default function OpportunityListPage({ opportunityType }) {
//     // --- State Variables ---
//     const [allJobPostings, setAllJobPostings] = useState([]);
//     const [loading, setLoading] = useState(true);
//     const [error, setError] = useState(null);
//     const [selectedJob, setSelectedJob] = useState(null);
//     const [searchQuery, setSearchQuery] = useState("");
//     const [quizStatuses, setQuizStatuses] = useState({});
//     const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//     const [profileStatus, setProfileStatus] = useState(null);
//     const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//     const [profileStatusError, setProfileStatusError] = useState(null);

//     const navigate = useNavigate();
//     // --- MODIFIED: Instantiate useLocation ---
//     const location = useLocation(); 
//     const id = localStorage.getItem("user_id");
//     const email = localStorage.getItem("email");
    
//     const currentConfig = postingTypeConfig[opportunityType];

//     // --- Data Fetching and Processing ---

//     const formatLocation = (locationData) => {
//       if (!locationData) return 'N/A';
//       try {
//         if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//           const validJsonString = locationData.replace(/'/g, '"');
//           const locations = JSON.parse(validJsonString);
//           return Array.isArray(locations) ? locations.join(', ') : locationData;
//         }
//         return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//       } catch (e) {
//         return locationData;
//       }
//     };

//     const openModal = (jobDetails) => {
//       setSelectedJob(jobDetails);
//       const modalElement = document.getElementById("staticBackdrop");
//       if (modalElement && window.bootstrap) {
//         const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//         modalInstance.show();
//       }
//     };

//     const fetchQuizStatuses = useCallback(async (jobs) => {
//       if (!id || !jobs?.length) return;
//       setIsFetchingStatuses(true);
//       const statusPromises = jobs.map(job =>
//         axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//           .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//           .catch(() => ({ jobId: job.job_id, taken: false }))
//       );
//       try {
//         const results = await Promise.all(statusPromises);
//         const newStatuses = results.reduce((acc, curr) => {
//           acc[curr.jobId] = curr.taken;
//           return acc;
//         }, {});
//         setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//       } catch (err) {
//         console.error("Error processing quiz statuses:", err);
//       } finally {
//         setIsFetchingStatuses(false);
//       }
//     }, [id]);

//     useEffect(() => {
//       setLoading(true);
//       axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//         .then(response => {
//           const jobs = response.data || [];
//           setAllJobPostings(jobs);
//           fetchQuizStatuses(jobs);
//         })
//         .catch(setError)
//         .finally(() => setLoading(false));
//     }, [fetchQuizStatuses]);

//     useEffect(() => {
//       if (id && email) {
//         setIsFetchingProfileStatus(true);
//         axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//           .then(response => setProfileStatus(response.data))
//           .catch(setProfileStatusError)
//           .finally(() => setIsFetchingProfileStatus(false));
//       } else if (id && !email) {
//         setProfileStatusError({ message: "User email not found." });
//       }
//     }, [id, email]);

//     const filteredJobs = useMemo(() => {
//       const lowerCaseQuery = searchQuery.toLowerCase();
//       return allJobPostings.filter(job => {
//         const isCorrectType = getNormalizedCategory(job) === opportunityType;
//         const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//         return isCorrectType && matchesSearch;
//       });
//     }, [allJobPostings, searchQuery, opportunityType]);

//     // --- MODIFIED: Event Handler ---
//     const handleApplyFromCard = (job) => {
//       if (!job?.job_id) {
//         Swal.fire("Error", "Cannot proceed without valid job data.", "error");
//         return;
//       }
//       if (isFetchingProfileStatus) {
//         Swal.fire({ title: "Checking Profile...", icon: "info", didOpen: () => Swal.showLoading() });
//         return;
//       }
//       if (profileStatusError) {
//         Swal.fire("Profile Check Failed", profileStatusError.message, "error");
//         return;
//       }
//       if (!profileStatus?.Resume_Filled) {
//         Swal.fire({
//           icon: "warning",
//           title: "Profile Incomplete",
//           html: "Please complete your profile (e.g., resume missing) before applying.",
//           confirmButtonText: "Go to My Profile",
//           showCancelButton: true,
//         }).then((result) => {
//           if (result.isConfirmed) navigate("/jobapplication");
//         });
//         return;
//       }
//       localStorage.setItem("selected_job_id", job.job_id);
//       localStorage.setItem("selectedJobTitle", job.jobtitle);

//       // --- THIS IS THE KEY CHANGE ---
//       // We navigate to the quiz form and pass the current path in the `state`.
//       navigate("/personalityquizform", { state: { from: location.pathname } });
//     };

//     // --- Render Functions (No changes below this line) ---
//     const renderJobCard = (job) => {
//       if (!job?.job_id) return null;
//       const hasTakenQuiz = quizStatuses[job.job_id] === true;
      
//       return (
//         <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//           <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//             <div className="card-body d-flex flex-column flex-grow-1">
//               <div>
//                 <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                   {job.jobtitle || "No Title"}
//                 </h5>
//                 <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//               </div>
//               <div className="mt-auto">
//                 <div className="mb-3">
//                   <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"} years</span></p>
//                   <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                   <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//                 </div>
//               </div>
//             </div>
//             <div className="card-footer bg-white border-top-0 pb-3">
//               <div className="d-flex justify-content-between">
//                 <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//                 <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}>
//                   {isFetchingProfileStatus || isFetchingStatuses ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       );
//     };

//     return (
//       <>
//         <DashNavbar />
//         <div className="wrapper">
//           <Sidebar />
//           <div className="main">
//             {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div></div>}
//             {error && <div className="container mt-4"><div className="alert alert-danger">Error loading job data: {error.message}</div></div>}
//             {!loading && !error && (
//               <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//                 <div className="text-center mb-5">
//                   <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                   <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                   <p className="lead text-muted">{currentConfig.description}</p>
//                 </div>

//                 <div className="row justify-content-center mb-5">
//                   <div className="col-lg-6 col-md-8">
//                     <div className="input-group shadow-sm">
//                       <span className="input-group-text"><i className="fas fa-search"></i></span>
//                       <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                     </div>
//                   </div>
//                 </div>

//                 <section className="mb-5">
//                   <div className="row">
//                     {filteredJobs.length > 0 ? (
//                       filteredJobs.map(job => renderJobCard(job))
//                     ) : (
//                       <div className="col-12 text-center text-muted mt-4">
//                           <i className="fas fa-inbox fa-3x mb-3"></i>
//                           <h4>No Results Found</h4>
//                           <p className="fst-italic">
//                               {searchQuery ? `No ${currentConfig.title.toLowerCase()} match your search.` : `No ${currentConfig.title.toLowerCase()} are available at this time.`}
//                           </p>
//                       </div>
//                     )}
//                   </div>
//                 </section>
//               </div>
//             )}
//           </div>
//         </div>

//         {/* Modal */}
//         <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//           <div className="modal-dialog modal-dialog-scrollable modal-lg">
//             <div className="modal-content">
//               <div className="modal-header">
//                 <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//                 <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//               </div>
//               <div className="modal-body">
//                 {selectedJob ? (
//                   <>
//                     <p>
//                       <strong>Type:</strong>{" "}
//                       <span className="badge fs-6" style={{ backgroundColor: currentConfig.color }}>
//                         {currentConfig.title}
//                       </span>
//                     </p>
//                     <p><strong>Role Type:</strong> {selectedJob.role_type || 'N/A'}</p>
//                     <p><strong>Experience:</strong> {selectedJob.experience || 'N/A'} years</p>
//                     <p><strong>Location(s):</strong> {formatLocation(selectedJob.job_location)}</p>
//                     <p><strong>Vacancies:</strong> {selectedJob.vacancies || 'N/A'}</p>
//                     <hr />
//                     <h6>Description:</h6>
//                     <div style={{ wordWrap: 'break-word', textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                   </>
//                 ) : <p>Loading...</p>}
//               </div>
//             </div>
//           </div>
//         </div>
//         <style jsx>{`
//           .job-card-hover {
//               transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
//               border: 1px solid #dee2e6;
//           }
//           .job-card-hover:hover {
//               transform: translateY(-5px);
//               box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15) !important;
//               border-color: ${currentConfig.color};
//           }
//           .card-title-clamp {
//               display: -webkit-box;
//               -webkit-line-clamp: 2;
//               -webkit-box-orient: vertical;
//               overflow: hidden;
//               text-overflow: ellipsis;
//               min-height: 3rem; 
//           }
//         `}</style>
//       </>
//     );
//   }

// // import React, { useState, useEffect, useCallback, useMemo } from "react";
// // import axios from "axios";
// // import { useNavigate } from "react-router-dom";
// // import DashNavbar from "./DashNavbar";
// // import "../css/dash.css";
// // import Sidebar from "./Sidebar";
// // import Swal from "sweetalert2";

// // // --- Helper Functions & Constants ---
// // const formatDate = (dateString) => {
// //   if (!dateString) return "Invalid Date";
// //   try {
// //     const options = { year: "numeric", month: "long", day: "numeric" };
// //     return new Date(dateString).toLocaleDateString(undefined, options);
// //   } catch (error) {
// //     return "Invalid Date";
// //   }
// // };

// // const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com/";

// // const postingTypeConfig = {
// //   Job_Opening: {
// //     title: "Job Openings",
// //     icon: "fa-solid fa-briefcase",
// //     color: "#0d6efd",
// //     description: "Find your next career move with our latest job openings.",
// //   },
// //   Consultancy_Opportunity: {
// //     title: "Consultancy Opportunities",
// //     icon: "fa-solid fa-user-tie",
// //     color: "#198754",
// //     description: "Explore expert roles and consultancy projects.",
// //   },
// //   Business_Opportunity: {
// //     title: "Business Opportunities",
// //     icon: "fa-solid fa-lightbulb",
// //     color: "#ffc107",
// //     description: "Discover new business ventures and partnership opportunities.",
// //   },
// // };

// // const getNormalizedCategory = (job) => {
// //   const category = job?.opening_post_as;
// //   if (!category || category === "Job Opening" || category === "Job_Opening") {
// //     return "Job_Opening";
// //   }
// //   return category;
// // };

// // // This is the new reusable component
// // export default function OpportunityListPage({ opportunityType }) {
// //   // --- State Variables ---
// //   const [allJobPostings, setAllJobPostings] = useState([]);
// //   const [loading, setLoading] = useState(true);
// //   const [error, setError] = useState(null);
// //   const [selectedJob, setSelectedJob] = useState(null);
// //   const [searchQuery, setSearchQuery] = useState("");
// //   const [quizStatuses, setQuizStatuses] = useState({});
// //   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
// //   const [profileStatus, setProfileStatus] = useState(null);
// //   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
// //   const [profileStatusError, setProfileStatusError] = useState(null);

// //   const navigate = useNavigate();
// //   const id = localStorage.getItem("user_id");
// //   const email = localStorage.getItem("email");
  
// //   const currentConfig = postingTypeConfig[opportunityType];

// //   // --- Data Fetching and Processing ---

// //   const formatLocation = (locationData) => {
// //     if (!locationData) return 'N/A';
// //     try {
// //       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
// //         const validJsonString = locationData.replace(/'/g, '"');
// //         const locations = JSON.parse(validJsonString);
// //         return Array.isArray(locations) ? locations.join(', ') : locationData;
// //       }
// //       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
// //     } catch (e) {
// //       return locationData;
// //     }
// //   };

// //   const openModal = (jobDetails) => {
// //     setSelectedJob(jobDetails);
// //     const modalElement = document.getElementById("staticBackdrop");
// //     if (modalElement && window.bootstrap) {
// //       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
// //       modalInstance.show();
// //     }
// //   };

// //   const fetchQuizStatuses = useCallback(async (jobs) => {
// //     if (!id || !jobs?.length) return;
// //     setIsFetchingStatuses(true);
// //     const statusPromises = jobs.map(job =>
// //       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
// //         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
// //         .catch(() => ({ jobId: job.job_id, taken: false }))
// //     );
// //     try {
// //       const results = await Promise.all(statusPromises);
// //       const newStatuses = results.reduce((acc, curr) => {
// //         acc[curr.jobId] = curr.taken;
// //         return acc;
// //       }, {});
// //       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
// //     } catch (err) {
// //       console.error("Error processing quiz statuses:", err);
// //     } finally {
// //       setIsFetchingStatuses(false);
// //     }
// //   }, [id]);

// //   useEffect(() => {
// //     setLoading(true);
// //     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
// //       .then(response => {
// //         const jobs = response.data || [];
// //         setAllJobPostings(jobs);
// //         fetchQuizStatuses(jobs);
// //       })
// //       .catch(setError)
// //       .finally(() => setLoading(false));
// //   }, [fetchQuizStatuses]);

// //   useEffect(() => {
// //     if (id && email) {
// //       setIsFetchingProfileStatus(true);
// //       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
// //         .then(response => setProfileStatus(response.data))
// //         .catch(setProfileStatusError)
// //         .finally(() => setIsFetchingProfileStatus(false));
// //     } else if (id && !email) {
// //       setProfileStatusError({ message: "User email not found." });
// //     }
// //   }, [id, email]);

// //   // --- Filtering Logic ---
// //   const filteredJobs = useMemo(() => {
// //     const lowerCaseQuery = searchQuery.toLowerCase();
// //     return allJobPostings.filter(job => {
// //       const isCorrectType = getNormalizedCategory(job) === opportunityType;
// //       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
// //       return isCorrectType && matchesSearch;
// //     });
// //   }, [allJobPostings, searchQuery, opportunityType]);

// //   // --- Event Handlers ---
// //   const handleApplyFromCard = (job) => {
// //     if (!job?.job_id) {
// //       Swal.fire("Error", "Cannot proceed without valid job data.", "error");
// //       return;
// //     }
// //     if (isFetchingProfileStatus) {
// //       Swal.fire({ title: "Checking Profile...", icon: "info", didOpen: () => Swal.showLoading() });
// //       return;
// //     }
// //     if (profileStatusError) {
// //       Swal.fire("Profile Check Failed", profileStatusError.message, "error");
// //       return;
// //     }
// //     if (!profileStatus?.Resume_Filled) {
// //       Swal.fire({
// //         icon: "warning",
// //         title: "Profile Incomplete",
// //         html: "Please complete your profile (e.g., resume missing) before applying.",
// //         confirmButtonText: "Go to My Profile",
// //         showCancelButton: true,
// //       }).then((result) => {
// //         if (result.isConfirmed) navigate("/jobapplication");
// //       });
// //       return;
// //     }
// //     localStorage.setItem("selected_job_id", job.job_id);
// //     localStorage.setItem("selectedJobTitle", job.jobtitle);
// //     navigate("/personalityquizform");
// //   };

// //   // --- Render Functions ---
// //   const renderJobCard = (job) => {
// //     if (!job?.job_id) return null;
// //     const hasTakenQuiz = quizStatuses[job.job_id] === true;
    
// //     return (
// //       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
// //         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
// //           <div className="card-body d-flex flex-column flex-grow-1">
// //             <div>
// //               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
// //                 {job.jobtitle || "No Title"}
// //               </h5>
// //               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
// //             </div>
// //             <div className="mt-auto">
// //               <div className="mb-3">
// //                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"} years</span></p>
// //                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
// //                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
// //               </div>
// //             </div>
// //           </div>
// //           <div className="card-footer bg-white border-top-0 pb-3">
// //             <div className="d-flex justify-content-between">
// //               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
// //               <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}>
// //                 {isFetchingProfileStatus || isFetchingStatuses ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
// //               </button>
// //             </div>
// //           </div>
// //         </div>
// //       </div>
// //     );
// //   };

// //   return (
// //     <>
// //       <DashNavbar />
// //       <div className="wrapper">
// //         <Sidebar />
// //         <div className="main">
// //           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div></div>}
// //           {error && <div className="container mt-4"><div className="alert alert-danger">Error loading job data: {error.message}</div></div>}
// //           {!loading && !error && (
// //             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
// //               <div className="text-center mb-5">
// //                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
// //                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
// //                 <p className="lead text-muted">{currentConfig.description}</p>
// //               </div>

// //               <div className="row justify-content-center mb-5">
// //                 <div className="col-lg-6 col-md-8">
// //                   <div className="input-group shadow-sm">
// //                     <span className="input-group-text"><i className="fas fa-search"></i></span>
// //                     <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
// //                   </div>
// //                 </div>
// //               </div>

// //               <section className="mb-5">
// //                 <div className="row">
// //                   {filteredJobs.length > 0 ? (
// //                     filteredJobs.map(job => renderJobCard(job))
// //                   ) : (
// //                     <div className="col-12 text-center text-muted mt-4">
// //                         <i className="fas fa-inbox fa-3x mb-3"></i>
// //                         <h4>No Results Found</h4>
// //                         <p className="fst-italic">
// //                             {searchQuery ? `No ${currentConfig.title.toLowerCase()} match your search.` : `No ${currentConfig.title.toLowerCase()} are available at this time.`}
// //                         </p>
// //                     </div>
// //                   )}
// //                 </div>
// //               </section>
// //             </div>
// //           )}
// //         </div>
// //       </div>

// //       {/* Modal */}
// //       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
// //         <div className="modal-dialog modal-dialog-scrollable modal-lg">
// //           <div className="modal-content">
// //             <div className="modal-header">
// //               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
// //               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
// //             </div>
// //             <div className="modal-body">
// //               {selectedJob ? (
// //                 <>
// //                   <p>
// //                     <strong>Type:</strong>{" "}
// //                     <span className="badge fs-6" style={{ backgroundColor: currentConfig.color }}>
// //                       {currentConfig.title}
// //                     </span>
// //                   </p>
// //                   <p><strong>Role Type:</strong> {selectedJob.role_type || 'N/A'}</p>
// //                   <p><strong>Experience:</strong> {selectedJob.experience || 'N/A'} years</p>
// //                   <p><strong>Location(s):</strong> {formatLocation(selectedJob.job_location)}</p>
// //                   <p><strong>Vacancies:</strong> {selectedJob.vacancies || 'N/A'}</p>
// //                   <hr />
// //                   <h6>Description:</h6>
// //                   <div style={{ wordWrap: 'break-word', textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
// //                 </>
// //               ) : <p>Loading...</p>}
// //             </div>
// //           </div>
// //         </div>
// //       </div>
// //       <style jsx>{`
// //         .job-card-hover {
// //             transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
// //             border: 1px solid #dee2e6;
// //         }
// //         .job-card-hover:hover {
// //             transform: translateY(-5px);
// //             box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15) !important;
// //             border-color: ${currentConfig.color};
// //         }
// //         .card-title-clamp {
// //             display: -webkit-box;
// //             -webkit-line-clamp: 2;
// //             -webkit-box-orient: vertical;
// //             overflow: hidden;
// //             text-overflow: ellipsis;
// //             min-height: 3rem; 
// //         }
// //       `}</style>
// //     </>
// //   );
// // }


// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// // --- MODIFIED: Import useLocation ---
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// // --- Helper Functions & Constants ---
// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "https://raasbackend.vetrinahealthcare.com";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//   const category = job?.opening_post_as;
//   if (!category || category === "Job Opening" || category === "Job_Opening") {
//     return "Job_Opening";
//   }
//   return category;
// };

// export default function OpportunityListPage({ opportunityType }) {
//   // --- State Variables ---
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   // --- MODIFIED: Instantiate useLocation ---
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   // --- Data Fetching and Processing ---

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   // --- FIX STARTS HERE ---
//   useEffect(() => {
//     setLoading(true);
//     setError(null); // Reset error state on new fetch

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         // Defensively check if the response data is an array.
//         // If the API returns an object like { "results": [...] }, you might need response.data.results
//         // This code safely handles cases where response.data is not an array.
//         const jobsArray = Array.isArray(response.data) ? response.data : [];
        
//         // Always set an array to the state to prevent the .filter error
//         setAllJobPostings(jobsArray);

//         // Only fetch statuses if there are actual jobs to check
//         if (jobsArray.length > 0) {
//           fetchQuizStatuses(jobsArray);
//         }
//       })
//       .catch(err => {
//         console.error("Failed to fetch job postings:", err);
//         setError(err); // Set the error state for UI feedback
//         setAllJobPostings([]); // IMPORTANT: Ensure state is a valid array even on error
//       })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);
//   // --- FIX ENDS HERE ---

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     } else if (id && !email) {
//       setProfileStatusError({ message: "User email not found." });
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     // This check is now safe because allJobPostings is guaranteed to be an array
//     if (!Array.isArray(allJobPostings)) {
//       return []; 
//     }
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   // --- MODIFIED: Event Handler ---
//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) {
//       Swal.fire("Error", "Cannot proceed without valid job data.", "error");
//       return;
//     }
//     if (isFetchingProfileStatus) {
//       Swal.fire({ title: "Checking Profile...", icon: "info", didOpen: () => Swal.showLoading() });
//       return;
//     }
//     if (profileStatusError) {
//       Swal.fire("Profile Check Failed", profileStatusError.message, "error");
//       return;
//     }
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile (e.g., resume missing) before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => {
//         if (result.isConfirmed) navigate("/jobapplication");
//       });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);

//     // --- THIS IS THE KEY CHANGE ---
//     // We navigate to the quiz form and pass the current path in the `state`.
//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   // --- Render Functions (No changes below this line) ---
//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;

//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"} years</span></p>
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}>
//                 {isFetchingProfileStatus || isFetchingStatuses ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div></div>}
//           {error && <div className="container mt-4"><div className="alert alert-danger">Error loading job data: {error.message}</div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>

//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <div className="input-group shadow-sm">
//                     <span className="input-group-text"><i className="fas fa-search"></i></span>
//                     <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                   </div>
//                 </div>
//               </div>

//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? (
//                     filteredJobs.map(job => renderJobCard(job))
//                   ) : (
//                     <div className="col-12 text-center text-muted mt-4">
//                         <i className="fas fa-inbox fa-3x mb-3"></i>
//                         <h4>No Results Found</h4>
//                         <p className="fst-italic">
//                             {searchQuery ? `No ${currentConfig.title.toLowerCase()} match your search.` : `No ${currentConfig.title.toLowerCase()} are available at this time.`}
//                         </p>
//                     </div>
//                   )}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Modal */}
//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body">
//               {selectedJob ? (
//                 <>
//                   <p>
//                     <strong>Type:</strong>{" "}
//                     <span className="badge fs-6" style={{ backgroundColor: currentConfig.color }}>
//                       {currentConfig.title}
//                     </span>
//                   </p>
//                   <p><strong>Role Type:</strong> {selectedJob.role_type || 'N/A'}</p>
//                   <p><strong>Experience:</strong> {selectedJob.experience || 'N/A'} years</p>
//                   <p><strong>Location(s):</strong> {formatLocation(selectedJob.job_location)}</p>
//                   <p><strong>Vacancies:</strong> {selectedJob.vacancies || 'N/A'}</p>
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word', textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .job-card-hover {
//             transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
//             border: 1px solid #dee2e6;
//         }
//         .job-card-hover:hover {
//             transform: translateY(-5px);
//             box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15) !important;
//             border-color: ${currentConfig.color};
//         }
//         .card-title-clamp {
//             display: -webkit-box;
//             -webkit-line-clamp: 2;
//             -webkit-box-orient: vertical;
//             overflow: hidden;
//             text-overflow: ellipsis;
//             min-height: 3rem; 
//         }
//       `}</style>
//     </>
//   );
// }












// import React, { useState, useEffect, useCallback, useMemo } from "react";
// import axios from "axios";
// import { useNavigate, useLocation } from "react-router-dom";
// import DashNavbar from "./DashNavbar";
// import "../css/dash.css";
// import Sidebar from "./Sidebar";
// import Swal from "sweetalert2";

// const formatDate = (dateString) => {
//   if (!dateString) return "Invalid Date";
//   try {
//     const options = { year: "numeric", month: "long", day: "numeric" };
//     return new Date(dateString).toLocaleDateString(undefined, options);
//   } catch (error) {
//     return "Invalid Date";
//   }
// };

// const API_BASE_URL = "http://127.0.0.1:8000";

// const postingTypeConfig = {
//   Job_Opening: {
//     title: "Job Openings",
//     icon: "fa-solid fa-briefcase",
//     color: "#0d6efd",
//     description: "Find your next career move with our latest job openings.",
//   },
//   Consultancy_Opportunity: {
//     title: "Consultancy Opportunities",
//     icon: "fa-solid fa-user-tie",
//     color: "#198754",
//     description: "Explore expert roles and consultancy projects.",
//   },
//   Business_Opportunity: {
//     title: "Business Opportunities",
//     icon: "fa-solid fa-lightbulb",
//     color: "#ffc107",
//     description: "Discover new business ventures and partnership opportunities.",
//   },
// };

// const getNormalizedCategory = (job) => {
//     // This function now primarily relies on the `opening_post_as` field
//     // which we normalize during the data fetching process.
//     return job?.opening_post_as;
// };


// export default function OpportunityListPage({ opportunityType }) {
//   const [allJobPostings, setAllJobPostings] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedJob, setSelectedJob] = useState(null);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [quizStatuses, setQuizStatuses] = useState({});
//   const [isFetchingStatuses, setIsFetchingStatuses] = useState(false);
//   const [profileStatus, setProfileStatus] = useState(null);
//   const [isFetchingProfileStatus, setIsFetchingProfileStatus] = useState(false);
//   const [profileStatusError, setProfileStatusError] = useState(null);

//   const navigate = useNavigate();
//   const location = useLocation();
//   const id = localStorage.getItem("user_id");
//   const email = localStorage.getItem("email");

//   const currentConfig = postingTypeConfig[opportunityType];

//   const formatLocation = (locationData) => {
//     if (!locationData) return 'N/A';
//     try {
//       if (typeof locationData === 'string' && locationData.startsWith('[') && locationData.endsWith(']')) {
//         const validJsonString = locationData.replace(/'/g, '"');
//         const locations = JSON.parse(validJsonString);
//         return Array.isArray(locations) ? locations.join(', ') : locationData;
//       }
//       return Array.isArray(locationData) ? locationData.join(', ') : locationData;
//     } catch (e) {
//       return locationData;
//     }
//   };

//   const openModal = (jobDetails) => {
//     setSelectedJob(jobDetails);
//     const modalElement = document.getElementById("staticBackdrop");
//     if (modalElement && window.bootstrap) {
//       const modalInstance = window.bootstrap.Modal.getOrCreateInstance(modalElement);
//       modalInstance.show();
//     }
//   };

//   const fetchQuizStatuses = useCallback(async (jobs) => {
//     if (!id || !jobs?.length) return;
//     setIsFetchingStatuses(true);
//     const statusPromises = jobs.map(job =>
//       axios.get(`${API_BASE_URL}/check_quiz_status/${id}/${job.job_id}/`)
//         .then(response => ({ jobId: job.job_id, taken: response.data.quiz_taken === true }))
//         .catch(() => ({ jobId: job.job_id, taken: false }))
//     );
//     try {
//       const results = await Promise.all(statusPromises);
//       const newStatuses = results.reduce((acc, curr) => {
//         acc[curr.jobId] = curr.taken;
//         return acc;
//       }, {});
//       setQuizStatuses(prev => ({ ...prev, ...newStatuses }));
//     } catch (err) {
//       console.error("Error processing quiz statuses:", err);
//     } finally {
//       setIsFetchingStatuses(false);
//     }
//   }, [id]);

//   useEffect(() => {
//     setLoading(true);
//     setError(null);

//     axios.get(`${API_BASE_URL}/JobPostingresultByStatus/`)
//       .then(response => {
//         const data = response.data;

//         // Normalize job postings
//         const jobPostings = (data.jobpostings || []).map(job => ({
//           ...job,
//           opening_post_as: 'Job_Opening' // Ensure correct category
//         }));

//         // Normalize business opportunities
//         const businessOpportunities = (data.business_opportunities || []).map(b => ({
//           ...b,
//           job_id: b.business_id,
//           jobtitle: b.business_title,
//           role_type: b.nature_of_business,
//           job_location: b.locations_type,
//           experience: b.experience || 'N/A',
//           vacancies: b.vacancies || 'N/A',
//           opening_post_as: 'Business_Opportunity'
//         }));

//         // Normalize technical consultants
//         const technicalConsultants = (data.technical_consultants || []).map(c => ({
//             ...c,
//             job_id: c.consultant_id,
//             jobtitle: c.title,
//             role_type: c.consultant_type,
//             job_location: c.locations,
//             experience: c.experience || 'N/A',
//             vacancies: c.vacancies || 'N/A',
//             opening_post_as: 'Consultancy_Opportunity'
//         }));

//         const allPostings = [
//           ...jobPostings,
//           ...businessOpportunities,
//           ...technicalConsultants
//         ];

//         setAllJobPostings(allPostings);

//         if (allPostings.length > 0) {
//           fetchQuizStatuses(allPostings);
//         }
//       })
//       .catch(err => {
//         console.error("Failed to fetch postings:", err);
//         setError(err);
//         setAllJobPostings([]);
//       })
//       .finally(() => setLoading(false));
//   }, [fetchQuizStatuses]);

//   useEffect(() => {
//     if (id && email) {
//       setIsFetchingProfileStatus(true);
//       axios.get(`${API_BASE_URL}/api/dashboard/${id}/status/?email=${email}`)
//         .then(response => setProfileStatus(response.data))
//         .catch(setProfileStatusError)
//         .finally(() => setIsFetchingProfileStatus(false));
//     } else if (id && !email) {
//       setProfileStatusError({ message: "User email not found." });
//     }
//   }, [id, email]);

//   const filteredJobs = useMemo(() => {
//     if (!Array.isArray(allJobPostings)) {
//       return [];
//     }
//     const lowerCaseQuery = searchQuery.toLowerCase();
//     return allJobPostings.filter(job => {
//       const isCorrectType = getNormalizedCategory(job) === opportunityType;
//       const matchesSearch = job?.jobtitle?.toLowerCase().includes(lowerCaseQuery);
//       return isCorrectType && matchesSearch;
//     });
//   }, [allJobPostings, searchQuery, opportunityType]);

//   const handleApplyFromCard = (job) => {
//     if (!job?.job_id) {
//       Swal.fire("Error", "Cannot proceed without valid job data.", "error");
//       return;
//     }
//     if (isFetchingProfileStatus) {
//       Swal.fire({ title: "Checking Profile...", icon: "info", didOpen: () => Swal.showLoading() });
//       return;
//     }
//     if (profileStatusError) {
//       Swal.fire("Profile Check Failed", profileStatusError.message, "error");
//       return;
//     }
//     if (!profileStatus?.Resume_Filled) {
//       Swal.fire({
//         icon: "warning",
//         title: "Profile Incomplete",
//         html: "Please complete your profile (e.g., resume missing) before applying.",
//         confirmButtonText: "Go to My Profile",
//         showCancelButton: true,
//       }).then((result) => {
//         if (result.isConfirmed) navigate("/jobapplication");
//       });
//       return;
//     }
//     localStorage.setItem("selected_job_id", job.job_id);
//     localStorage.setItem("selectedJobTitle", job.jobtitle);
//     // Persist the posting type so the quiz form can fetch the correct questions set
//     if (job.opening_post_as) {
//       localStorage.setItem("selected_posting_type", job.opening_post_as);
//     }

//     navigate("/personalityquizform", { state: { from: location.pathname } });
//   };

//   const renderJobCard = (job) => {
//     if (!job?.job_id) return null;
//     const hasTakenQuiz = quizStatuses[job.job_id] === true;

//     return (
//       <div key={job.job_id} className="col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-4 d-flex">
//         <div className="card h-100 w-100 d-flex flex-column shadow-sm job-card-hover">
//           <div className="card-body d-flex flex-column flex-grow-1">
//             <div>
//               <h5 className="card-title fw-bold mb-1 card-title-clamp" title={job.jobtitle}>
//                 {job.jobtitle || "No Title"}
//               </h5>
//               <p className="text-muted small">Posted on {formatDate(job.created_at)}</p>
//             </div>
//             <div className="mt-auto">
//               <div className="mb-3">
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-briefcase fa-fw me-2 text-secondary" /><span>{job.experience || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center"><i className="fa-solid fa-clock fa-fw me-2 text-secondary" /><span>{job.role_type || "N/A"}</span></p>
//                 <p className="card-text small d-flex align-items-center text-truncate" title={formatLocation(job.job_location)}><i className="fa-solid fa-location-dot fa-fw me-2 text-secondary" /><span>{formatLocation(job.job_location)}</span></p>
//               </div>
//             </div>
//           </div>
//           <div className="card-footer bg-white border-top-0 pb-3">
//             <div className="d-flex justify-content-between">
//               <button type="button" className="btn btn-outline-info w-100 me-2" onClick={() => openModal(job)}>Details</button>
//               <button type="button" className={`btn w-100 ms-1 ${hasTakenQuiz ? 'btn-secondary' : 'btn-success'}`} onClick={() => !hasTakenQuiz && handleApplyFromCard(job)} disabled={hasTakenQuiz || isFetchingStatuses || isFetchingProfileStatus}>
//                 {isFetchingProfileStatus || isFetchingStatuses ? <span className="spinner-border spinner-border-sm" /> : hasTakenQuiz ? "Applied" : "Apply"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>
//     );
//   };

//   return (
//     <>
//       <DashNavbar />
//       <div className="wrapper">
//         <Sidebar />
//         <div className="main">
//           {loading && <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}><div className="spinner-border text-primary" style={{ width: "3rem", height: "3rem" }}></div></div>}
//           {error && <div className="container mt-4"><div className="alert alert-danger">Error loading data: {error.message}</div></div>}
//           {!loading && !error && (
//             <div className="container-fluid jobresult p-lg-4 p-md-3 p-sm-2">
//               <div className="text-center mb-5">
//                 <i className={`${currentConfig.icon} fa-3x mb-3`} style={{ color: currentConfig.color }}></i>
//                 <h2 className="display-5 gradient-text">{currentConfig.title}</h2>
//                 <p className="lead text-muted">{currentConfig.description}</p>
//               </div>

//               <div className="row justify-content-center mb-5">
//                 <div className="col-lg-6 col-md-8">
//                   <div className="input-group shadow-sm">
//                     <span className="input-group-text"><i className="fas fa-search"></i></span>
//                     <input type="search" className="form-control form-control-lg" placeholder={`Search ${currentConfig.title}...`} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
//                   </div>
//                 </div>
//               </div>

//               <section className="mb-5">
//                 <div className="row">
//                   {filteredJobs.length > 0 ? (
//                     filteredJobs.map(job => renderJobCard(job))
//                   ) : (
//                     <div className="col-12 text-center text-muted mt-4">
//                         <i className="fas fa-inbox fa-3x mb-3"></i>
//                         <h4>No Results Found</h4>
//                         <p className="fst-italic">
//                             {searchQuery ? `No ${currentConfig.title.toLowerCase()} match your search.` : `No ${currentConfig.title.toLowerCase()} are available at this time.`}
//                         </p>
//                     </div>
//                   )}
//                 </div>
//               </section>
//             </div>
//           )}
//         </div>
//       </div>

//       <div className="modal fade" id="staticBackdrop" data-bs-backdrop="static" tabIndex="-1">
//         <div className="modal-dialog modal-dialog-scrollable modal-lg">
//           <div className="modal-content">
//             <div className="modal-header">
//               <h5 className="modal-title">{selectedJob?.jobtitle || "Details"}</h5>
//               <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={() => setSelectedJob(null)}></button>
//             </div>
//             <div className="modal-body">
//               {selectedJob ? (
//                 <>
//                   <p>
//                     <strong>Type:</strong>{" "}
//                     <span className="badge fs-6" style={{ backgroundColor: currentConfig.color }}>
//                       {currentConfig.title}
//                     </span>
//                   </p>
//                   <p><strong>Role Type:</strong> {selectedJob.role_type || 'N/A'}</p>
//                   <p><strong>Experience:</strong> {selectedJob.experience || 'N/A'}</p>
//                   <p><strong>Location(s):</strong> {formatLocation(selectedJob.job_location)}</p>
//                   <p><strong>Vacancies:</strong> {selectedJob.vacancies || 'N/A'}</p>
//                   <hr />
//                   <h6>Description:</h6>
//                   <div style={{ wordWrap: 'break-word', textAlign: 'justify' }} dangerouslySetInnerHTML={{ __html: selectedJob.description || 'No description provided.' }} />
//                 </>
//               ) : <p>Loading...</p>}
//             </div>
//           </div>
//         </div>
//       </div>
//       <style jsx>{`
//         .job-card-hover {
//             transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
//             border: 1px solid #dee2e6;
//         }
//         .job-card-hover:hover {
//             transform: translateY(-5px);
//             box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15) !important;
//             border-color: ${currentConfig.color};
//         }
//         .card-title-clamp {
//             display: -webkit-box;
//             -webkit-line-clamp: 2;
//             -webkit-box-orient: vertical;
//             overflow: hidden;
//             text-overflow: ellipsis;
//             min-height: 3rem; 
//         }
//       `}</style>
//     </>
//   );
// }